/**
 * TransportActionsModal
 *
 * Two-tab modal for transport actions on a selected transport bundle:
 * 1. Submit via a transport destination (forwards TRANSPORT_ID + DESCRIPTION)
 * 2. Create a new transport destination (name + transporter node definition)
 */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from 'redux/store';
import { createTransportDestination, fetchTransportDestinations, submitTransportDestination, Transport } from 'redux/slices/transportsSlice';
import { EditNodeDefinitionReference, NodeDefinitionReference } from 'edit/reference/EditNodeDefinitionReference';

interface Props {
  show: boolean;
  onClose: () => void;
  transport: Transport;
}

const TransportActionsModal: React.FC<Props> = ({ show, onClose, transport }) => {
  const dispatch = useAppDispatch();
  const { transportDestinations, loading } = useSelector((s: RootState) => s.transports);

  const [activeTab, setActiveTab] = useState<'transport' | 'create'>('transport');
  const [description, setDescription] = useState('');
  const [selectedDestinationId, setSelectedDestinationId] = useState<number | ''>('');
  const [destinationName, setDestinationName] = useState('');
  const [nodeDefinition, setNodeDefinition] = useState<NodeDefinitionReference | null>(null);
  const [submitResult, setSubmitResult] = useState<{
    node_instance_id: number;
    status: string;
  } | null>(null);

  useEffect(() => {
    if (!show) return;
    dispatch(fetchTransportDestinations());
    setActiveTab('transport');
    setDescription('');
    setSelectedDestinationId('');
    setDestinationName('');
    setNodeDefinition(null);
    setSubmitResult(null);
  }, [show, dispatch, transport.id]);

  const handleTransport = async () => {
    if (!selectedDestinationId || !description.trim()) return;
    const result = await dispatch(
      submitTransportDestination({
        transportDestinationId: Number(selectedDestinationId),
        transport_id: transport.id,
        description: description.trim(),
      })
    );
    if (submitTransportDestination.fulfilled.match(result)) {
      setSubmitResult({
        node_instance_id: result.payload.node_instance.node_instance_id,
        status: result.payload.node_instance.status,
      });
    }
  };

  const handleCreateDestination = async () => {
    if (!destinationName.trim() || !nodeDefinition) return;
    const result = await dispatch(
      createTransportDestination({
        name: destinationName.trim(),
        transporter_node_definition_id: nodeDefinition.node_definition_id,
        transporter_node_definition_name: nodeDefinition.name,
        transporter_zone_name: nodeDefinition.zone_name,
      })
    );
    if (createTransportDestination.fulfilled.match(result)) {
      setSelectedDestinationId(result.payload.id);
      setDestinationName('');
      setNodeDefinition(null);
      setActiveTab('transport');
    }
  };

  const isBusy = loading.fetchTransportDestinations || loading.createTransportDestination || loading.submitTransportDestination;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Transport Actions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Transport bundle: <strong>{transport.name}</strong> (ID {transport.id})
        </p>

        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab((key as 'transport' | 'create') || 'transport')} className="mb-3">
          <Tab eventKey="transport" title="Transport">
            <Form.Group className="mb-3">
              <Form.Label>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="SVN commit message" />
              <Form.Text className="text-muted">Forwarded to the transporter node as the DESCRIPTION parameter.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Transport destination <span className="text-danger">*</span>
              </Form.Label>
              {loading.fetchTransportDestinations ? (
                <div className="py-2">
                  <Spinner size="sm" />
                </div>
              ) : (
                <Form.Select value={selectedDestinationId} onChange={(e) => setSelectedDestinationId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">-- Select a transport destination --</option>
                  {transportDestinations.map((destination) => (
                    <option key={destination.id} value={destination.id}>
                      {destination.name}
                      {destination.transporter_node_definition_name
                        ? ` (${destination.transporter_zone_name ?? '?'}/${destination.transporter_node_definition_name})`
                        : ''}
                    </option>
                  ))}
                </Form.Select>
              )}
              {transportDestinations.length === 0 && !loading.fetchTransportDestinations && (
                <Form.Text className="text-muted">No transport destinations yet. Create one on the next tab.</Form.Text>
              )}
            </Form.Group>

            {submitResult && (
              <Alert variant="success" className="mb-0">
                Submitted node instance #{submitResult.node_instance_id} ({submitResult.status}).
              </Alert>
            )}
          </Tab>

          <Tab eventKey="create" title="Create destination">
            <Form.Group className="mb-3">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control type="text" value={destinationName} onChange={(e) => setDestinationName(e.target.value)} placeholder="e.g. SVN Root" />
            </Form.Group>

            <EditNodeDefinitionReference label="Transporter node definition" mandatory value={nodeDefinition} onChange={setNodeDefinition} />
            <Form.Text className="text-muted">The node definition that downloads and commits transport bundles (for example an SVN commit node).</Form.Text>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isBusy}>
          Close
        </Button>
        {activeTab === 'transport' ? (
          <Button variant="primary" onClick={handleTransport} disabled={isBusy || !selectedDestinationId || !description.trim()}>
            {loading.submitTransportDestination ? <Spinner size="sm" /> : 'Transport'}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleCreateDestination} disabled={isBusy || !destinationName.trim() || !nodeDefinition}>
            {loading.createTransportDestination ? <Spinner size="sm" /> : 'Create destination'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TransportActionsModal;
